import { CancelToken } from "axios";
import { has, isEqual, keyBy, keys, map } from "lodash";

import { BandSettingsDataKeys, IBand, IEditItem } from "Bands/types";
import { getBandSettingSlugFromKey, getBandSettingTypeFromKey } from "Bands/utils";
import { API_ENDPOINTS, API_ERROR_CODES } from "shared/constants";
import { APIService } from "shared/services";
import { IAPIError, IDataKeys, IJSONAPIItem } from "shared/types";

interface IJSONAPIBand {
  [key: string]: IJSONAPIItem;
}

export interface IJSONAPIBandCollection {
  [IDataKeys.band]: IJSONAPIBand;
  meta: {
    [key: string]: IJSONAPIItem;
  };
  pagination: any;
}

export class BandService {
  static createBand = (band: Partial<IBand>): Promise<IJSONAPIBandCollection> =>
    new Promise(async (resolve, reject) => {
      try {
        const response: unknown = await APIService.post({
          path: API_ENDPOINTS.BANDS,
          data: band,
        });
        resolve(response as IJSONAPIBandCollection);
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.POST_BAND);
        reject(error);
      }
    });

  static getBand = (id: string, cancelToken?: CancelToken): Promise<IJSONAPIBandCollection> =>
    new Promise(async (resolve, reject) => {
      try {
        const path = API_ENDPOINTS.BAND(id);
        const band: unknown = await APIService.get({ path, cancelToken });
        resolve(band as IJSONAPIBandCollection);
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.GET_BAND);
        reject(error);
      }
    });

  static getBands = (
    queryParams?: string | { [key: string]: string | number | undefined | string[] }
  ): Promise<IJSONAPIBandCollection> =>
    new Promise(async (resolve, reject) => {
      try {
        const bandIds: unknown = await APIService.get({
          path: API_ENDPOINTS.BANDS,
          data: queryParams,
        });
        resolve(bandIds as IJSONAPIBandCollection);
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.GET_BANDS);
        reject(error);
      }
    });

  static getAllBands = (
    queryParams?: string | { [key: string]: string | number | undefined | string[] }
  ): Promise<IJSONAPIBandCollection> =>
    new Promise(async (resolve, reject) => {
      try {
        const bandUsers: unknown = await APIService.get({
          path: API_ENDPOINTS.BANDS,
          data: queryParams,
        });

        resolve(bandUsers as IJSONAPIBandCollection);
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.GET_BANDS);
        reject(error);
      }
    });

  static editBand = (bandId: string, band: Partial<IBand>): Promise<IJSONAPIBandCollection> =>
    new Promise(async (resolve, reject) => {
      try {
        const patchedBand: unknown = await APIService.patch({
          path: API_ENDPOINTS.BAND(bandId),
          data: band,
        });
        resolve(patchedBand as IJSONAPIBandCollection);
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.PATCH_BAND);
        reject(error);
      }
    });

  static deleteBand = (bandId: string): Promise<void> =>
    new Promise(async (resolve, reject) => {
      try {
        await APIService.delete({
          path: API_ENDPOINTS.BAND(bandId),
        });
        resolve();
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.DELETE_BAND);
        reject(error);
      }
    });

  static editBandSettings = ({
    bandId,
    oldItems,
    newItems,
    dataKey,
  }: {
    bandId: string;
    oldItems: IEditItem[];
    newItems: IEditItem[];
    dataKey: BandSettingsDataKeys;
  }) =>
    new Promise(async (resolve, reject) => {
      try {
        // check if something has changed
        if (isEqual(newItems, oldItems)) {
          resolve(newItems);
        }

        const slug = getBandSettingSlugFromKey(dataKey);
        const type = getBandSettingTypeFromKey(dataKey);
        const oldItemMap = keyBy(oldItems, "id");

        const newList = await Promise.all(
          newItems.map(async newItem => {
            const { id, ...postItem } = newItem;

            const oldItem = oldItemMap[id];
            // item is new
            if (!oldItem) {
              // return api response at it has the proper id
              const res = await APIService.post({
                path: API_ENDPOINTS.ADD_BAND_SETTING(slug),
                data: { ...postItem, band: API_ENDPOINTS.BAND(bandId) },
              });
              const { attributes, id } = res?.[type]?.[keys(res?.[type])[0]] || {};
              return { ...attributes, id };
            }

            const { id: oldItemId, ...oldItemProps } = oldItem;
            const { id: newItemId, ...newItemProps } = newItem;
            // item was updated
            if (!isEqual(oldItemProps, newItemProps)) {
              try {
                await APIService.patch({
                  path: id,
                  data: newItem,
                });
              } catch (error) {
                (error as IAPIError).errorCodes?.push(API_ERROR_CODES.PATCH_BAND_SETTING);
                reject(error);
              }
            }

            return newItem;
          })
        );
        const newItemMap = keyBy(newList, "id");

        // check for deleted items
        await Promise.all(
          oldItems.map(async ({ id }) => {
            if (!has(newItemMap, id)) {
              try {
                await APIService.delete({
                  path: id,
                });
                return;
              } catch (error) {
                (error as IAPIError).errorCodes?.push(API_ERROR_CODES.DELETE_BAND_SETTING);
                reject(error);
              }
            }
          })
        );

        const orderedKeys = map(newItemMap, ({ id }) => id);
        // do reorder api call
        try {
          const data = await APIService.patch({
            path: API_ENDPOINTS.BAND(bandId),
            data: { [dataKey]: orderedKeys },
          });

          resolve(data);
        } catch (error) {
          (error as IAPIError).errorCodes?.push(API_ERROR_CODES.PATCH_BAND);
          reject(error);
        }
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.POST_BAND_SETTING);
        reject(error);
      }
    });
}
